import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { StatusTag } from "../../components/common/registerUtils";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  API_ASSET_DISPOSALS,
  API_ASSET_DISPOSALS_EXPORT,
} from "../../utils/apis";
import {
  CAN_APPROVE_ASSET_DISPOSAL,
  CAN_DISPOSE_ASSETS,
} from "../../utils/constants";
import {
  createViewAction,
  formatAmount,
  formatDate,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_ASSET_DISPOSALS } from "../../utils/urls";
import {
  APPROVAL_STATUSES,
  AssetSummary,
  DISPOSAL_TYPES,
  approvalSection,
  assetExport,
  assetLabel,
  auditFields,
  createAssetApprovalActionForm,
  createdAtFilter,
  isDisposed,
  toFilterOptions,
  toSelectItems,
  useAssetPermissions,
  useAssetPicker,
} from "./assetUtils";

const SALE = "sale";

const proceedsOrEmpty = (amount: any) =>
  amount === null || amount === undefined ? null : formatAmount(amount);

export const useAssetDisposals = ({ status }: { status?: string } = {}) => {
  const can = useAssetPermissions();

  const tableColumns = () => [
    { id: "disposal_id", header: "Disposal ID" },
    { id: "asset", header: "Asset", filter: { type: "text" } },
    {
      id: "disposal_type",
      header: "Disposal Type",
      filter: { type: "select", options: toFilterOptions(DISPOSAL_TYPES) },
    },
    { id: "disposal_date", header: "Disposal Date" },
    { id: "proceeds", header: "Proceeds" },
    { id: "reason", header: "Reason" },
    {
      id: "status",
      header: "Status",
      ...(status
        ? {}
        : {
            filter: {
              type: "select",
              options: toFilterOptions(APPROVAL_STATUSES),
            },
          }),
    },
    { id: "created_at", header: "Requested On", filter: createdAtFilter },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      asset: assetLabel(item),
      disposal_type: DISPOSAL_TYPES[item?.disposal_type] ?? item?.disposal_type,
      proceeds: proceedsOrEmpty(item?.proceeds),
      status: <StatusTag status={item?.status} />,
      created_at: formatDate(item?.created_at),
      actions: (
        <FormActions
          actions={[createViewAction(URL_ASSET_DISPOSALS, item?.id)]}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Disposal Details",
        fields: {
          disposal_id: item?.disposal_id,
          asset: assetLabel(item),
          disposal_type:
            DISPOSAL_TYPES[item?.disposal_type] ?? item?.disposal_type,
          disposal_date: item?.disposal_date,
          proceeds: proceedsOrEmpty(item?.proceeds),
          reason: item?.reason,
          status: <StatusTag status={item?.status} />,
        },
      },
      approvalSection(item),
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Asset Disposal",
    tableColumns,
    tableData,
    viewData,
    activityFilter: "asset_disposals",
    formRoute: can(CAN_DISPOSE_ASSETS)
      ? getForm(URL_ASSET_DISPOSALS)
      : undefined,
    createTitle: "New Disposal Request",
    exportData: assetExport(API_ASSET_DISPOSALS_EXPORT, "asset_disposals"),
  };
};

export const useAssetDisposalsForm = ({
  form,
  searchParams,
  updateFormValue,
}: TFormHookParams & { searchParams?: Record<string, string> } = {}) => {
  const { search: assetSearch, asset } = useAssetPicker(form?.asset);

  useEffect(() => {
    if (searchParams?.asset) updateFormValue?.("asset", searchParams.asset);
  }, []);

  const blocked = isDisposed(asset);
  const isSale = form?.disposal_type === SALE;

  const sections: IFormSection[] = [
    {
      title: "Disposal Request",
      fields: [
        {
          field_name: "asset",
          label: "Asset",
          type: "select",
          search: assetSearch,
          props: { required: true, placeholder: "Search asset by name" },
        },
        {
          field_name: "asset_summary",
          type: "component",
          visible: !!asset,
          component: (
            <AssetSummary
              asset={asset}
              warning={
                blocked ? "This asset has already been disposed." : undefined
              }
            />
          ),
        },
        {
          field_name: "disposal_type",
          label: "Disposal Type",
          type: "select",
          options_data: toSelectItems(DISPOSAL_TYPES),
          props: { required: true, placeholder: "Select disposal type" },
        },
        {
          field_name: "proceeds",
          label: "Proceeds",
          type: "number",
          visible: isSale,
          props: { placeholder: "Amount realised from the sale" },
        },
        {
          field_name: "disposal_date",
          label: "Disposal Date",
          type: "date",
          props: { required: true, disableFuture: true },
        },
        {
          field_name: "reason",
          label: "Reason",
          type: "textarea",
          props: { required: true, placeholder: "Reason for disposal" },
        },
      ],
    },
  ];

  const onSubmit = (payload: any) => ({
    api: API_ASSET_DISPOSALS,
    params: {
      asset: payload?.asset,
      disposal_type: payload?.disposal_type,
      reason: payload?.reason,
      disposal_date: payload?.disposal_date,
      proceeds: isSale ? payload?.proceeds ?? null : null,
    },
  });

  return {
    sections,
    title: "Asset Disposal",
    redirect: getRegister(URL_ASSET_DISPOSALS),
    onSubmit,
    submitDisabled: blocked,
  };
};

export const useAssetDisposalActionForm = createAssetApprovalActionForm(
  API_ASSET_DISPOSALS,
  CAN_APPROVE_ASSET_DISPOSAL,
);
